/**
 * Subscription Selector Component - KNESKO Style
 * Handles subscription/one-time purchase toggle on product pages
 * with button-style frequency selector
 */

if (!customElements.get('subscription-selector')) {
  class SubscriptionSelector extends HTMLElement {
    constructor() {
      super();
      this.sellingPlanInput = this.querySelector('.subscription-selector__input');
      this.radioButtons = this.querySelectorAll('.subscription-selector__radio');
      this.frequencyButtons = this.querySelectorAll('.subscription-selector__frequency-btn');
      this.frequencyContainer = this.querySelector('[data-frequency-selector]');
      this.sellingPlansData = this.getSellingPlansData();
    }

    connectedCallback() {
      // Radio button listeners (subscribe vs one-time)
      this.radioButtons.forEach(radio => {
        radio.addEventListener('change', this.onPurchaseOptionChange.bind(this));
      });

      // Frequency button listeners
      this.frequencyButtons.forEach(btn => {
        btn.addEventListener('click', this.onFrequencyButtonClick.bind(this));
      });

      // Initialize with default selection
      this.initializeSelection();

      // Listen for variant changes
      document.addEventListener('variant:change', this.onVariantChange.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange.bind(this));
    }

    getSellingPlansData() {
      const jsonScript = this.querySelector('[data-selling-plans-json]');
      if (jsonScript) {
        try {
          return JSON.parse(jsonScript.textContent);
        } catch (e) {
          console.error('Error parsing selling plans data:', e);
          return null;
        }
      }
      return null;
    }

    initializeSelection() {
      const checkedRadio = this.querySelector('.subscription-selector__radio:checked');
      if (checkedRadio) {
        if (checkedRadio.value === 'subscribe') {
          // Get the active frequency button's selling plan
          const activeBtn = this.querySelector('.subscription-selector__frequency-btn.is-active');
          if (activeBtn) {
            this.updateSellingPlan(activeBtn.dataset.sellingPlanId);
          } else {
            this.updateSellingPlan(checkedRadio.dataset.sellingPlanId);
          }
        } else {
          this.updateSellingPlan('');
        }
        this.updateButtonText(checkedRadio.value);
      }
    }

    onPurchaseOptionChange(event) {
      const selectedOption = event.target.value;
      let sellingPlanId = '';

      if (selectedOption === 'subscribe') {
        // Get selling plan from active frequency button or first button
        const activeBtn = this.querySelector('.subscription-selector__frequency-btn.is-active');
        if (activeBtn) {
          sellingPlanId = activeBtn.dataset.sellingPlanId;
        } else {
          const firstBtn = this.querySelector('.subscription-selector__frequency-btn');
          if (firstBtn) {
            sellingPlanId = firstBtn.dataset.sellingPlanId;
            firstBtn.classList.add('is-active');
          } else {
            sellingPlanId = event.target.dataset.sellingPlanId;
          }
        }
        
        // Show frequency selector
        if (this.frequencyContainer) {
          this.frequencyContainer.hidden = false;
        }
      } else {
        // One-time purchase - no selling plan
        sellingPlanId = '';
        
        // Hide frequency selector
        if (this.frequencyContainer) {
          this.frequencyContainer.hidden = true;
        }
      }

      this.updateSellingPlan(sellingPlanId);
      this.updateButtonText(selectedOption);
      this.updatePriceDisplay(selectedOption, sellingPlanId);
    }

    onFrequencyButtonClick(event) {
      event.preventDefault();
      const clickedBtn = event.currentTarget;
      const sellingPlanId = clickedBtn.dataset.sellingPlanId;

      // Update active state
      this.frequencyButtons.forEach(btn => btn.classList.remove('is-active'));
      clickedBtn.classList.add('is-active');

      // Update selling plan
      this.updateSellingPlan(sellingPlanId);
      this.updatePriceDisplay('subscribe', sellingPlanId);
    }

    updateSellingPlan(sellingPlanId) {
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = sellingPlanId || '';
      }

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('selling-plan:change', {
        bubbles: true,
        detail: { sellingPlanId }
      }));
    }

    updateButtonText(purchaseType) {
      const section = this.closest('product-info, [data-section-id]');
      const productForm = section?.querySelector('.product-form__submit');
      if (!productForm) return;

      const buttonText = productForm.querySelector('[data-button-text]');
      if (!buttonText) return;

      const subscribeText = buttonText.dataset.subscribeText || 'Subscribe';
      const originalText = buttonText.dataset.originalText || 'Add to cart';

      if (purchaseType === 'subscribe') {
        buttonText.textContent = subscribeText;
      } else {
        buttonText.textContent = originalText;
      }
    }

    updatePriceDisplay(purchaseType, sellingPlanId) {
      if (!this.sellingPlansData) return;

      const variantPrice = this.sellingPlansData.variant_price;
      let displayPrice = variantPrice;
      let discountPercentage = 0;

      if (purchaseType === 'subscribe' && sellingPlanId) {
        const sellingPlan = this.sellingPlansData.selling_plans.find(
          sp => sp.id.toString() === sellingPlanId.toString()
        );

        if (sellingPlan) {
          if (sellingPlan.price_adjustment_type === 'percentage') {
            discountPercentage = sellingPlan.price_adjustment_value;
            const discount = variantPrice * (discountPercentage / 100);
            displayPrice = variantPrice - discount;
          } else if (sellingPlan.price_adjustment_type === 'fixed_amount') {
            displayPrice = variantPrice - sellingPlan.price_adjustment_value;
            discountPercentage = Math.round((sellingPlan.price_adjustment_value / variantPrice) * 100);
          }
        }
      }

      // Update the subscription option price display
      const priceContainer = this.querySelector('[data-option="subscribe"] .subscription-selector__price');
      if (priceContainer && purchaseType === 'subscribe') {
        const salePrice = priceContainer.querySelector('.subscription-selector__price-sale');
        if (salePrice) {
          salePrice.textContent = this.formatMoney(displayPrice);
        }
      }

      // Update the badge
      const badge = this.querySelector('.subscription-selector__badge');
      if (badge && discountPercentage > 0) {
        badge.textContent = `Save up to ${discountPercentage}%`;
      }

      // Update the main product price display
      const section = this.closest('product-info, [data-section-id], .product');
      if (section) {
        const priceWrapper = section.querySelector('.price');
        if (priceWrapper) {
          const regularContainer = priceWrapper.querySelector('.price__regular');
          const saleContainer = priceWrapper.querySelector('.price__sale');

          if (purchaseType === 'subscribe' && displayPrice < variantPrice) {
            // Show sale pricing: compare at = original, sale = subscription price
            if (regularContainer) regularContainer.style.display = 'none';
            if (saleContainer) {
              saleContainer.style.display = '';
              const compareEl = saleContainer.querySelector('.price-item--regular');
              const saleEl = saleContainer.querySelector('.price-item--sale');
              if (compareEl) {
                const priceMoney = compareEl.querySelector('price-money bdi') || compareEl.querySelector('bdi') || compareEl;
                priceMoney.textContent = this.formatMoney(variantPrice);
              }
              if (saleEl) {
                const priceMoney = saleEl.querySelector('price-money bdi') || saleEl.querySelector('bdi') || saleEl;
                priceMoney.textContent = this.formatMoney(displayPrice);
              }
            }
            priceWrapper.classList.remove('price--no-compare');
            priceWrapper.classList.add('price--on-sale');
          } else {
            // Show regular pricing
            if (regularContainer) regularContainer.style.display = '';
            if (saleContainer) saleContainer.style.display = 'none';
            priceWrapper.classList.add('price--no-compare');
            priceWrapper.classList.remove('price--on-sale');
          }
        }
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      // Use the store's money format if available
      if (typeof Shopify !== 'undefined' && Shopify.locale && Shopify.locale.moneyFormat) {
        return Shopify.locale.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
      }
      return `$${amount}`;
    }

    onVariantChange(event) {
      // Handle variant changes - update prices and selling plan availability
      const variant = event.detail?.variant;
      if (!variant) return;

      // Re-fetch selling plans data for new variant if needed
      // This would require updating the JSON data
    }
  }

  customElements.define('subscription-selector', SubscriptionSelector);
}

/**
 * Cart Subscription Selector
 * Uses event delegation to work with dynamically rendered cart content
 */
(function() {
  // Cart subscription helper functions
  const CartSubscriptionHelper = {
    async updateCartItem(key, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) throw new Error('Failed to update cart item');
      return response.json();
    },

    async addToCart(variantId, quantity, sellingPlanId) {
      const body = {
        id: parseInt(variantId),
        quantity: quantity
      };
      if (sellingPlanId && sellingPlanId !== '0' && sellingPlanId !== '') {
        body.selling_plan = parseInt(sellingPlanId);
      }
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Failed to add item to cart');
      return response.json();
    },

    refreshCart() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      const sectionsToUpdate = ['mini-cart', 'cart-notification-product', 'cart-icon-bubble'];
      fetch(`/cart?sections=${sectionsToUpdate.join(',')}`)
        .then(response => response.json())
        .then(data => {
          sectionsToUpdate.forEach(sectionId => {
            const sectionContent = data[sectionId];
            if (sectionContent) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionContent, 'text/html');
              
              if (sectionId === 'mini-cart') {
                const miniCart = document.querySelector('.mini-cart');
                const newMiniCart = doc.querySelector('.mini-cart');
                if (miniCart && newMiniCart) {
                  miniCart.innerHTML = newMiniCart.innerHTML;
                  miniCart.classList.toggle('is-empty', newMiniCart.classList.contains('is-empty'));
                }
              }
              
              if (sectionId === 'cart-icon-bubble') {
                const bubble = document.getElementById('cart-icon-bubble');
                const newBubble = doc.getElementById('cart-icon-bubble');
                if (bubble && newBubble) {
                  bubble.innerHTML = newBubble.innerHTML;
                }
              }
            }
          });
        })
        .catch(error => {
          console.error('Error refreshing cart:', error);
          window.location.reload();
        });
    },

    setLoading(container, isLoading) {
      container.classList.toggle('is-loading', isLoading);
    }
  };

  // Event delegation for cart subscription checkboxes
  document.addEventListener('change', async function(event) {
    const checkbox = event.target.closest('[data-subscribe-checkbox]');
    if (!checkbox) return;

    const container = checkbox.closest('.cart-subscription-selector');
    if (!container) return;

    const frequencyContainer = container.querySelector('[data-frequency-container]');
    const frequencySelect = container.querySelector('[data-frequency-select]');
    const isChecked = checkbox.checked;

    if (isChecked) {
      // Show frequency dropdown
      if (frequencyContainer) {
        frequencyContainer.hidden = false;
      }
      
      // Convert to subscription
      if (frequencySelect) {
        const sellingPlanId = frequencySelect.value;
        const itemKey = container.dataset.itemKey;
        const variantId = container.dataset.variantId;
        const quantity = parseInt(container.dataset.quantity) || 1;

        CartSubscriptionHelper.setLoading(container, true);

        try {
          await CartSubscriptionHelper.updateCartItem(itemKey, 0);
          await CartSubscriptionHelper.addToCart(variantId, quantity, sellingPlanId);
          CartSubscriptionHelper.refreshCart();
        } catch (error) {
          console.error('Error converting to subscription:', error);
          checkbox.checked = false;
          if (frequencyContainer) frequencyContainer.hidden = true;
          CartSubscriptionHelper.setLoading(container, false);
        }
      }
    } else {
      // Hide frequency dropdown
      if (frequencyContainer) {
        frequencyContainer.hidden = true;
      }
    }
  });

  // Event delegation for frequency select changes
  document.addEventListener('change', async function(event) {
    const select = event.target.closest('[data-frequency-select]');
    if (!select) return;

    const container = select.closest('.cart-subscription-selector');
    if (!container) return;

    const checkbox = container.querySelector('[data-subscribe-checkbox]');
    if (!checkbox || !checkbox.checked) return;

    const sellingPlanId = select.value;
    const itemKey = container.dataset.itemKey;
    const variantId = container.dataset.variantId;
    const quantity = parseInt(container.dataset.quantity) || 1;

    CartSubscriptionHelper.setLoading(container, true);

    try {
      await CartSubscriptionHelper.updateCartItem(itemKey, 0);
      await CartSubscriptionHelper.addToCart(variantId, quantity, sellingPlanId);
      CartSubscriptionHelper.refreshCart();
    } catch (error) {
      console.error('Error changing subscription frequency:', error);
      CartSubscriptionHelper.setLoading(container, false);
    }
  });
})();

/**
 * Cart Subscription Upgrade Card - Button Handler
 * Handles the "UPGRADE" button click on the ELM-style subscription card
 */
(function() {
  document.addEventListener('click', async function(event) {
    const btn = event.target.closest('[data-sub-upgrade-btn]');
    if (!btn) return;

    const card = btn.closest('.cart-sub-upgrade');
    if (!card) return;

    const itemKey = btn.dataset.itemKey;
    const variantId = btn.dataset.variantId;
    const quantity = parseInt(btn.dataset.quantity) || 1;

    // Check for frequency selector override
    const freqSelect = card.querySelector('[data-sub-frequency]');
    const sellingPlanId = freqSelect ? freqSelect.value : btn.dataset.sellingPlanId;

    if (!sellingPlanId || !itemKey || !variantId) return;

    // Show loading state
    const btnText = btn.querySelector('.cart-sub-upgrade__btn-text');
    const btnLoading = btn.querySelector('.cart-sub-upgrade__btn-loading');
    btn.disabled = true;
    if (btnText) btnText.hidden = true;
    if (btnLoading) btnLoading.hidden = false;

    try {
      // Remove existing one-time item
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      if (!removeResponse.ok) throw new Error('Failed to remove item');

      // Add back with selling plan
      const addBody = {
        id: parseInt(variantId),
        quantity: quantity,
        selling_plan: parseInt(sellingPlanId)
      };
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addBody)
      });
      if (!addResponse.ok) throw new Error('Failed to add subscription item');

      // Refresh the page/cart
      // Try dispatching cart:refresh for mini-cart, then reload for full cart page
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // If we're on the full cart page, reload to reflect changes
      if (document.querySelector('cart-items')) {
        window.location.reload();
      } else {
        // Mini-cart: fetch updated sections
        const sectionsToUpdate = ['mini-cart', 'cart-icon-bubble'];
        const sectionsResponse = await fetch('/cart?sections=' + sectionsToUpdate.join(','));
        const sectionsData = await sectionsResponse.json();

        sectionsToUpdate.forEach(function(sectionId) {
          const sectionContent = sectionsData[sectionId];
          if (!sectionContent) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionContent, 'text/html');

          if (sectionId === 'mini-cart') {
            const miniCart = document.querySelector('.mini-cart');
            const newMiniCart = doc.querySelector('.mini-cart');
            if (miniCart && newMiniCart) {
              miniCart.innerHTML = newMiniCart.innerHTML;
              miniCart.classList.toggle('is-empty', newMiniCart.classList.contains('is-empty'));
            }
          }

          if (sectionId === 'cart-icon-bubble') {
            const bubble = document.getElementById('cart-icon-bubble');
            const newBubble = doc.getElementById('cart-icon-bubble');
            if (bubble && newBubble) {
              bubble.innerHTML = newBubble.innerHTML;
            }
          }
        });
      }
    } catch (error) {
      console.error('Error upgrading to subscription:', error);
      btn.disabled = false;
      if (btnText) btnText.hidden = false;
      if (btnLoading) btnLoading.hidden = true;
    }
  });

  // Update selling plan ID when frequency dropdown changes
  document.addEventListener('change', function(event) {
    const freqSelect = event.target.closest('[data-sub-frequency]');
    if (!freqSelect) return;

    const card = freqSelect.closest('.cart-sub-upgrade');
    if (!card) return;

    const btn = card.querySelector('[data-sub-upgrade-btn]');
    if (btn) {
      btn.dataset.sellingPlanId = freqSelect.value;
    }
  });
})();
